import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import BottomSheet from './ui/BottomSheet';
import Button from './ui/Button';
import { useExpenseStore } from '../stores/expenseStore';
import { TOAST } from 'pairtab-shared/constants';

type EditableExpense = {
  id: string;
  amount_cents: number;
  description?: string | null;
  action: 'split' | 'keep';
  split_ratio: number;
  created_at: string;
  category?: { name?: string; emoji?: string };
};

interface ExpenseEditSheetProps {
  expense: EditableExpense | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void | Promise<void>;
}

const toDateInputValue = (dateString: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export default function ExpenseEditSheet({
  expense,
  isOpen,
  onClose,
  onSaved,
}: ExpenseEditSheetProps) {
  const { updateExpense, deleteExpense } = useExpenseStore();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [action, setAction] = useState<'split' | 'keep'>('split');
  const [splitRatio, setSplitRatio] = useState(50);
  const [date, setDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!expense || !isOpen) return;

    setAmount((expense.amount_cents / 100).toFixed(2));
    setDescription(expense.description || '');
    setAction(expense.action || 'split');
    setSplitRatio(expense.action === 'keep' ? 0 : expense.split_ratio ?? 50);
    setDate(toDateInputValue(expense.created_at));
  }, [expense, isOpen]);

  const summary = useMemo(() => {
    const amountCents = Math.round((parseFloat(amount) || 0) * 100);
    const yourShare = Math.round(amountCents * (splitRatio / 100));
    const otherShare = amountCents - yourShare;
    return {
      yourShare: (yourShare / 100).toFixed(2),
      otherShare: (otherShare / 100).toFixed(2),
    };
  }, [amount, splitRatio]);

  const handleSave = async () => {
    if (!expense || isSaving) return;

    const amountCents = Math.round((parseFloat(amount) || 0) * 100);
    if (!amountCents || amountCents <= 0) {
      toast.error(TOAST.errorAmountRequired);
      return;
    }

    if (!date) {
      toast.error('Choose a date');
      return;
    }

    setIsSaving(true);
    try {
      await updateExpense(expense.id, {
        amount_cents: amountCents,
        description: description.trim(),
        action,
        split_ratio: action === 'keep' ? 0 : splitRatio,
        created_at: new Date(`${date}T12:00:00`).toISOString(),
      });

      await onSaved?.();
      toast.success(TOAST.expenseUpdated);
      onClose();
    } catch (err: any) {
      toast.error(err.message || TOAST.errorGeneric);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!expense || isSaving) return;
    if (!window.confirm('Delete this expense?')) return;

    setIsSaving(true);
    try {
      await deleteExpense(expense.id);
      await onSaved?.();
      toast.success(TOAST.expenseArchived);
      onClose();
    } catch (err: any) {
      toast.error(err.message || TOAST.errorGeneric);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Edit Expense">
      <div className="space-y-4">
        {expense?.category && (
          <div className="flex items-center gap-3 rounded-xl bg-muted/10 px-4 py-3">
            <span className="text-2xl">{expense.category.emoji || '📦'}</span>
            <div>
              <p className="font-medium">{expense.category.name || 'Uncategorized'}</p>
              <p className="text-xs text-muted">Only your own logged expenses can be edited</p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium text-muted">Amount</label>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(event) => {
              const nextValue = event.target.value.replace(/[^0-9.]/g, '');
              if (nextValue.split('.').length <= 2) {
                setAmount(nextValue);
              }
            }}
            className="w-full bg-card border border-border rounded-2xl py-4 px-4 text-3xl font-bold text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 tabular-nums"
            placeholder="0.00"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-muted">Date</label>
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="input-field"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-muted">Description</label>
            <span className="text-xs text-muted">{description.length}/500</span>
          </div>
          <input
            type="text"
            value={description}
            onChange={(event) => {
              if (event.target.value.length <= 500) {
                setDescription(event.target.value);
              }
            }}
            className="input-field"
            placeholder="Optional note"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-muted">Split</label>
          <div className="grid grid-cols-2 gap-2">
            <Button variant={action === 'split' ? 'primary' : 'secondary'} onClick={() => setAction('split')}>
              Split
            </Button>
            <Button variant={action === 'keep' ? 'primary' : 'secondary'} onClick={() => setAction('keep')}>
              Keep
            </Button>
          </div>
        </div>

        {action === 'split' && (
          <div className="space-y-3 rounded-xl bg-muted/10 p-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted">Your Share (%)</label>
              <input
                type="range"
                min="0"
                max="100"
                value={splitRatio}
                onChange={(event) => setSplitRatio(Number(event.target.value))}
                className="w-full accent-primary"
              />
              <div className="flex items-center justify-between text-sm font-medium">
                <span>{splitRatio}%</span>
                <span>{100 - splitRatio}%</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg bg-card px-3 py-2">
                <p className="text-muted">Your share</p>
                <p className="font-semibold tabular-nums">${summary.yourShare}</p>
              </div>
              <div className="rounded-lg bg-card px-3 py-2">
                <p className="text-muted">Other share</p>
                <p className="font-semibold tabular-nums">${summary.otherShare}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
        <Button variant="danger" onClick={handleDelete} disabled={isSaving} className="w-full">
          Delete Expense
        </Button>
      </div>
    </BottomSheet>
  );
}
